import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { EMPTY, finalize, forkJoin, Observable, of, switchMap, catchError, tap, throwError, merge, fromEvent, filter } from 'rxjs';
import { BoardV3RealtimeService } from '../../board-v3/board-v3-realtime.service';
import { WebSocketService } from '../../../services/gateway.service';
import { VchasnoKasaService } from '../../../services/vchasno-kasa.service';
import { FiscalNotificationService } from '../../../services/fiscal-notification.service';
import { TranslationManagerService } from '../../../core/i18n';
import { CashRegister, FiscalOperation, FiscalSettings, ReceiptPreview } from '../../prro/prro.types';
import { IChatItem } from '../../../interfaces';
import { BoardPaymentService } from '../../../services/board-payment.service';
import { BoardService } from '../../../services/board.service';
import { compatiblePaymentService } from '../payment-compatibility.utils';
import { getCurrencySymbol, parseNumber, toOptions } from '../../warehouse/warehouse.utils';
import {
  CardPayment,
  CardPaymentsBlock,
  CardPaymentUpdatePayload,
  CardPaymentXDataPayload,
  PaymentInvoiceDetails,
  PaymentServiceRecord,
  PaymentTypeRecord,
} from '../payments.types';
import {
  BANK_QR_NAME_MAX_LENGTH,
  BANK_QR_PURPOSE_MAX_LENGTH,
  isTaxId,
  isUaIban,
  normalizeIban,
  PAYMENT_COMMENT_MAX_LENGTH,
  parsePaymentAmount,
} from '../payments.utils';

/** A payment is saved as pending or paid; canceling is a separate action that asks for a confirmation */
const FORM_STATUSES = ['pending', 'paid'] as const;
type FormPaymentStatus = (typeof FORM_STATUSES)[number];

/** `app-select` option of a payment type or service */
interface PaymentOption {
  id: string;
  name: string;
}

/** The values of the form as the API takes them */
interface PaymentFields {
  payment_type_id: string;
  payment_service_id: string | null;
  amount: number;
  status: FormPaymentStatus;
  paid_at: string | null;
  comment: string | null;
  /** The invoice details to send: null takes the invoice away, undefined leaves it to the backend */
  invoice: PaymentInvoiceDetails | null | undefined;
}

/** Errors are keys of `input_errors`, the way `app-input` shows them */
const notBlank: ValidatorFn = (control) => (String(control.value ?? '').trim() ? null : { incorrect: true });
const ibanValidator: ValidatorFn = (control) => (!control.value || isUaIban(control.value) ? null : { iban: true });
const taxIdValidator: ValidatorFn = (control) => (!control.value || isTaxId(control.value) ? null : { tax_id: true });

/** The details as the invoice stores them, to compare a saved invoice with the form */
const toInvoiceDetails = (details: PaymentInvoiceDetails): PaymentInvoiceDetails => ({
  legal_name: details.legal_name.trim(),
  tax_id: details.tax_id.trim(),
  iban: normalizeIban(details.iban),
  purpose: details.purpose.trim(),
});

/**
 * Reuses the shared `input_errors` texts: a malformed amount is `price`, zero is `min`;
 * an empty one is left to `Validators.required`
 */
const paymentAmountValidator: ValidatorFn = (control) => {
  const text = control.value as string;
  if (!text || parsePaymentAmount(text) !== null) return null;

  return parseNumber(text, 2) === null ? { price: true } : { min: { min: 0.01, actual: 0 } };
};

const pad = (value: number): string => String(value).padStart(2, '0');

/** The local date and time of a moment as the two pickers hold them: `yyyy-mm-dd` and `HH:mm` */
function toPickerValues(iso: string | null): { date: string; time: string } {
  const instant = iso ? new Date(iso) : null;
  if (!instant || Number.isNaN(instant.getTime())) return { date: '', time: '' };

  return {
    date: `${instant.getFullYear()}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}`,
    time: `${pad(instant.getHours())}:${pad(instant.getMinutes())}`,
  };
}

/** The moment the two pickers hold, to the minute: none without a date, midnight without a time */
function fromPickerValues(date: string | null, time: string | null): string | null {
  if (!date) return null;

  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = (time || '00:00').split(':').map(Number);
  const instant = new Date(year, month - 1, day, hours, minutes);
  return Number.isNaN(instant.getTime()) ? null : instant.toISOString();
}

/** Someone else changed the payment (409) or it is gone: what is on the screen is out of date and must be reloaded */
export const isStalePaymentError = (code: string | undefined): boolean =>
  code === 'CARD_PAYMENT_STATUS_CONFLICT' || code === 'CARD_PAYMENT_NOT_FOUND';

/**
 * Creates a payment of a card or edits a pending one: type, service, amount in the card currency, status, the date
 * it was paid and a comment. It sits in a stacked sidebar and closes with the payments block the backend returned.
 */
@Component({
  selector: 'app-payment-form',
  templateUrl: './payment-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class PaymentFormComponent implements OnInit {
  @Input({ required: true }) cardId!: string;
  /** The pending payment to edit, a new payment when it is not set */
  @Input() payment: CardPayment | null = null;
  @Input() defaultAmount: number | null = null;
  /** The currency of the card: a payment has none of its own */
  @Input() confirmOnly = false;
  @Input() chats: IChatItem[] = [];
  @Input() currency: string | null = null;
  /** The payments of the card after the save, or reloaded when the payment turned out to be out of date */
  @Output() close = new EventEmitter<CardPaymentsBlock | void>();

  private readonly fiscal = inject(VchasnoKasaService);
  private readonly realtime = inject(BoardV3RealtimeService);
  private readonly boardService = inject(BoardService);
  private readonly ws = inject(WebSocketService);
  private readonly fiscalNotifications = inject(FiscalNotificationService);
  private readonly translations = inject(TranslationManagerService);
  protected connected = false;
  protected fiscalDenied = false;
  protected registers: CashRegister[] = [];
  protected fiscalSettings: FiscalSettings | null = null;
  protected receipt: ReceiptPreview | null = null;
  protected previewLoading = false;
  protected previewFailed = false;
  protected previewChanged = false;
  private lastPreviewRevision: string | null = null;
  protected cashRegisterId = new FormControl<string | null>(null);
  protected chatId = new FormControl<string | null>(null);
  protected readonly metadataFields = [
    'paysys',
    'rrn',
    'cardmask',
    'term_id',
    'bank_name',
    'bank_id',
    'auth_code',
    'purchase_dt',
  ];
  protected readonly metadata: Record<string, string> = {};
  private previewRequest?: import('rxjs').Subscription;
  private readonly confirmationKey = crypto.randomUUID();
  protected get chatOptions(): { id: string; name: string }[] {
    return this.chats.map((c) => ({ id: c.id, name: `${c.title || c.id} · ${c.external_type || c.type}` }));
  }
  protected get settlementKind(): 'sale' | 'advance' | 'final' | null {
    if (this.settlementBlocked) return null;
    if (this.receipt?.settlement_status === 'final') return 'final';
    const amount = parsePaymentAmount(this.form.controls.amount.value);
    return amount !== null && this.receipt && amount < this.receipt.total ? 'advance' : 'sale';
  }
  protected get settlementBlocked(): boolean {
    return !!this.receipt?.settlement_status && ['advance_pending', 'receipt_pending', 'completed', 'unlinked', 'refunded'].includes(this.receipt.settlement_status);
  }
  protected get settlementAmountError(): string | null {
    const amount = parsePaymentAmount(this.form.controls.amount.value);
    if (!this.receipt || this.settlementBlocked || amount === null) return null;
    if (!(this.receipt.paid_total ?? 0) && amount < this.receipt.total && new Set(this.receipt.rows.map((row) => row.taxgrp)).size > 1) {
      return 'prro.errors.FISCAL_ADVANCE_MIXED_TAX_GROUPS_UNSUPPORTED';
    }
    const remaining = this.receipt.remaining ?? this.receipt.total;
    if (Math.round(amount * 100) > Math.round(remaining * 100)) return 'prro.errors.FISCAL_PAYMENT_EXCEEDS_REMAINING';
    if ((this.receipt.paid_total ?? 0) > 0 && Math.round(amount * 100) !== Math.round(remaining * 100)) return 'prro.errors.FISCAL_FINAL_AMOUNT_REQUIRED';
    return null;
  }

  protected get needsCardMetadata(): boolean {
    const value = this.form.getRawValue();
    const mappings = this.fiscalSettings?.payment_mappings ?? [];
    const mapping =
      mappings.find(
        (m) => m.payment_type_id === value.payment_type_id && m.payment_service_id === value.payment_service_id,
      ) ?? mappings.find((m) => m.payment_type_id === value.payment_type_id && !m.payment_service_id);
    return !!mapping && (mapping.payment_code === 2 || mapping.requires_card_metadata);
  }
  private readonly paymentService = inject(BoardPaymentService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form = new FormGroup({
    payment_type_id: new FormControl<string | null>(null, [Validators.required]),
    payment_service_id: new FormControl<string | null>(null),
    amount: new FormControl('', { nonNullable: true, validators: [Validators.required, paymentAmountValidator] }),
    status: new FormControl<FormPaymentStatus>('pending', { nonNullable: true }),
    paid_date: new FormControl<string | null>(''),
    /** The time belongs to a date: it is disabled until there is one */
    paid_time: new FormControl<string | null>({ value: '', disabled: true }),
    comment: new FormControl<string | null>('', [Validators.maxLength(PAYMENT_COMMENT_MAX_LENGTH)]),
    /** An invoice can be issued for a service connected to the `bank_qr` integration */
    invoice_enabled: new FormControl(false, { nonNullable: true }),
    /** The details of the service, changed for this payment only; disabled while there is no invoice */
    invoice: new FormGroup({
      legal_name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, notBlank, Validators.maxLength(BANK_QR_NAME_MAX_LENGTH)],
      }),
      tax_id: new FormControl('', { nonNullable: true, validators: [Validators.required, taxIdValidator] }),
      iban: new FormControl('', { nonNullable: true, validators: [Validators.required, ibanValidator] }),
      purpose: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, notBlank, Validators.maxLength(BANK_QR_PURPOSE_MAX_LENGTH)],
      }),
    }),
  });

  protected readonly statusOptions = toOptions(FORM_STATUSES, 'board_payments.status.');
  protected readonly commentMaxLength = PAYMENT_COMMENT_MAX_LENGTH;
  protected readonly purposeMaxLength = BANK_QR_PURPOSE_MAX_LENGTH;
  protected readonly digitsOnly = /\d/;

  /** Active types and services, plus the ones the edited payment has even when they were deactivated since */
  protected typeOptions: PaymentOption[] = [];
  private types: PaymentTypeRecord[] = [];
  protected get serviceOptions(): PaymentOption[] {
    const type = this.types.find((item) => item.id === this.form.controls.payment_type_id.value);
    return this.services.filter((service) => (service.is_active || service.id === this.payment?.payment_service_id)
      && compatiblePaymentService(type, service, this.currency)).map(({ id, name }) => ({ id, name }));
  }
  protected get manualProviderPayment(): boolean {
    const code = this.types.find((type) => type.id === this.form.controls.payment_type_id.value)?.fiscal_code;
    return code != null && code >= 16 && code <= 20;
  }
  /** Active services with their integration settings, plus the one of the edited payment */
  private services: PaymentServiceRecord[] = [];
  protected loading = true;
  protected loadFailed = false;
  protected saving = false;
  protected submittedReceipt: FiscalOperation | null = null;

  /** The date of the payment as the pickers hold it, to tell an untouched date from a changed one */
  private initialPaidAt: string | null = null;

  protected get currencySymbol(): string {
    return getCurrencySymbol(this.currency);
  }

  /** The picked service when it is connected to the `bank_qr` integration */
  protected get invoiceService(): PaymentServiceRecord | null {
    const id = this.form.controls.payment_service_id.value;
    const service = id ? this.services.find((item) => item.id === id) : null;
    return service?.x_data?.integration === 'bank_qr' ? service : null;
  }

  /** A BankQR invoice is in hryvnias only, the backend issues none for another card currency */
  protected get isInvoiceCurrency(): boolean {
    return !this.currency || this.currency === 'UAH';
  }

  ngOnInit(): void {
    if (this.payment?.status === 'pending') this.submittedReceipt = this.payment.fiscal_operation ?? null;
    if (this.payment) this.patchPayment(this.payment);
    else if (this.defaultAmount !== null && Number.isFinite(this.defaultAmount) && this.defaultAmount > 0) {
      this.form.controls.amount.setValue(this.defaultAmount.toFixed(2));
    }
    if (this.confirmOnly) this.form.controls.status.setValue('paid');
    this.translations.loadModuleTranslations('prro').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.cashRegisterId.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadPreview());
    merge(
      this.realtime.cardDetailUpdated$(this.cardId),
      this.realtime.connectionRestored$(),
      this.ws.fiscalOperationCompleted(),
      fromEvent(window, 'focus'),
      fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible')),
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadPreview());
    if (this.chats.length === 1) this.chatId.setValue(this.chats[0].id);
    this.form.controls.paid_date.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((date) => this.syncPaidTime(date));
    this.form.controls.payment_type_id.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.syncService());
    this.form.controls.payment_service_id.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onServicePicked());
    this.form.controls.invoice_enabled.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncInvoice());
    this.syncInvoice();
    this.load();
  }

  protected load(): void {
    this.loading = true;
    this.loadFailed = false;
    forkJoin({
      types: this.paymentService.getPaymentTypes(true),
      services: this.paymentService.getPaymentServices(),
      integration: this.fiscal.availability(this.cardId).pipe(
        switchMap((availability) =>
          availability.connected
            ? forkJoin({ registers: this.fiscal.registers(), settings: this.fiscal.settings() }).pipe(
                switchMap((result) => of({ connected: true, denied: false, ...result })),
              )
            : of({
                connected: false,
                registers: [] as CashRegister[],
                settings: null as FiscalSettings | null,
                denied: false,
              }),
        ),
        catchError((error) =>
          error?.status === 403
            ? of({
                connected: true,
                denied: true,
                registers: [] as CashRegister[],
                settings: null as FiscalSettings | null,
              })
            : throwError(() => error),
        ),
      ),
    })
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ types, services, integration }) => {
          this.connected = integration.connected;
          this.fiscalDenied = integration.denied;
          this.registers = integration.registers.filter((r) => r.is_active);
          this.fiscalSettings = integration.settings;
          if (this.registers.length === 1) this.cashRegisterId.setValue(this.registers[0].id);
          if (this.confirmOnly) {
            this.form.controls.amount.disable();
            this.form.controls.comment.disable();
            this.form.controls.status.disable();
          }
          this.typeOptions = this.getOptions(types, this.payment?.payment_type);
          this.types = types;
          this.services = services;
          this.syncService();
          this.syncInvoice();
        },
        error: (error) => {
          this.loadFailed = true;
          this.paymentService.error(error);
        },
      });
  }

  protected submit(): void {
    if (this.saving) return;
    if (this.submittedReceipt) {
      this.saveRequest(this.fiscal.retryReceipt(this.submittedReceipt).pipe(
        switchMap((operation) => this.fiscal.waitForReceipt(operation.id)),
        switchMap(() => this.paymentService.getCardPayments(this.cardId)),
      ), true);
      return;
    }
    this.form.markAllAsTouched();
    const value = this.form.getRawValue();
    const amount = parsePaymentAmount(value.amount);
    if (this.form.invalid || this.saving || amount === null || !value.payment_type_id) return;

    const fields: PaymentFields = {
      payment_type_id: value.payment_type_id,
      payment_service_id: value.payment_service_id,
      amount,
      status: value.status,
      paid_at: fromPickerValues(value.paid_date, value.paid_time),
      comment: value.comment?.trim() || null,
      invoice: this.getInvoiceDetails(),
    };
    const receiptConfirmation = this.connected && fields.status === 'paid';
    if (receiptConfirmation && this.settlementAmountError) {
      this.paymentService.fail(this.settlementAmountError);
      return;
    }
    if (
      receiptConfirmation &&
      (!this.cashRegisterId.value ||
        !this.receipt ||
        this.previewLoading ||
        this.previewFailed ||
        this.settlementBlocked ||
        (this.chats.length && !this.chatId.value))
    ) {
      this.fiscalNotifications.showError({ code: 'FISCAL_RECEIPT_CONFIGURATION_REQUIRED' });
      return;
    }
    if (
      receiptConfirmation &&
      this.needsCardMetadata &&
      this.metadataFields.filter((field) => field !== 'purchase_dt').some((field) => !this.metadata[field]?.trim())
    ) {
      this.fiscalNotifications.showError({ code: 'FISCAL_CARD_METADATA_REQUIRED' });
      return;
    }
    const cardMetadata = this.needsCardMetadata
      ? Object.fromEntries(
          Object.entries(this.metadata)
            .filter(([, value]) => value?.trim())
            .map(([key, value]) => [key, value.trim()]),
        )
      : {};
    const registerId = this.cashRegisterId.value!;
    const chatId = this.chatId.value;
    // Keep the revision the user confirmed even if realtime refreshes while a shift is opening.
    const previewRevision = this.receipt?.preview_revision ?? '';
    const confirm = () =>
      this.fiscal.confirm(
        this.cardId,
        this.payment?.id ?? null,
        fields,
        registerId,
        this.confirmationKey,
        cardMetadata,
        chatId,
        previewRevision,
      ).pipe(
        tap((result) => {
          this.submittedReceipt = result.fiscal_operation;
          this.payment = result.payments.find((payment) => payment.id === result.payment_id) ?? this.payment;
        }),
        switchMap((result) => this.fiscal.waitForReceipt(result.fiscal_operation.id)),
        switchMap(() => this.paymentService.getCardPayments(this.cardId)),
      );
    const pendingUpdate =
      receiptConfirmation && this.payment
        ? this.update(this.payment, { ...fields, status: 'pending' })
        : null;
    const confirmation$ = receiptConfirmation
      ? pendingUpdate
        ? pendingUpdate.pipe(switchMap((block) => {
            const saved = block.payments.find((payment) => payment.id === this.payment?.id);
            if (!saved || saved.payment_type_id !== fields.payment_type_id || saved.payment_service_id !== fields.payment_service_id || saved.amount !== fields.amount) {
              return throwError(() => ({ error: { errors: [{ code: 'CARD_PAYMENT_STATUS_CONFLICT' }] } }));
            }
            this.payment = saved;
            const paidAt = toPickerValues(saved.paid_at);
            this.initialPaidAt = fromPickerValues(paidAt.date, paidAt.time);
            return confirm();
          }))
        : confirm()
      : this.payment
        ? this.update(this.payment, fields)
        : this.create(fields);
    const request$ = receiptConfirmation
      ? this.fiscal.ensureOpenShift(registerId).pipe(switchMap((opened) => opened ? confirmation$! : EMPTY))
      : confirmation$;
    if (!request$) {
      this.close.emit();
      return;
    }

    this.saveRequest(request$, receiptConfirmation);
  }

  private saveRequest(request$: Observable<CardPaymentsBlock>, receiptConfirmation: boolean): void {
    this.saving = true;
    request$.pipe(finalize(() => (this.saving = false)), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (block) => {
        if (!receiptConfirmation) this.paymentService.success('board_payments.form.saved');
        this.close.emit(block);
      },
      error: (error) => this.handleError(error),
    });
  }

  protected loadPreview(): void {
    this.previewRequest?.unsubscribe();
    this.previewLoading = false;
    const registerId = this.cashRegisterId.value;
    this.receipt = null;
    this.previewFailed = false;
    if (!registerId) return;
    this.previewLoading = true;
    this.previewRequest = this.fiscal
      .preview(this.cardId, registerId)
      .pipe(
        finalize(() => (this.previewLoading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (receipt) => {
          if (this.cashRegisterId.value !== registerId) return;
          if (this.lastPreviewRevision && this.lastPreviewRevision !== receipt.preview_revision) {
            this.previewChanged = true;
            this.boardService.requestCardRefresh(this.cardId);
          }
          this.lastPreviewRevision = receipt.preview_revision;
          this.receipt = receipt;
        },
        error: (error) => {
          this.previewFailed = true;
          this.paymentService.error(error);
        },
      });
  }

  /** Only what is filled in: the backend takes an omitted field as not set */
  private create(fields: PaymentFields): Observable<CardPaymentsBlock> {
    const { payment_service_id, paid_at, comment, invoice, ...required } = fields;
    return this.paymentService.createCardPayment(this.cardId, {
      ...required,
      ...(payment_service_id ? { payment_service_id } : {}),
      ...(paid_at ? { paid_at } : {}),
      ...(comment ? { comment } : {}),
      ...(invoice !== undefined ? { x_data: { invoice } } : {}),
    });
  }

  /** Only what differs from the payment (`null` clears the service, the date, the comment); none when nothing does */
  private update(payment: CardPayment, fields: PaymentFields): Observable<CardPaymentsBlock> | null {
    const changes: CardPaymentUpdatePayload = {};
    if (fields.payment_type_id !== payment.payment_type_id) changes.payment_type_id = fields.payment_type_id;
    if (fields.payment_service_id !== payment.payment_service_id) {
      changes.payment_service_id = fields.payment_service_id;
    }
    if (fields.amount !== payment.amount) changes.amount = fields.amount;
    if (fields.status !== payment.status) changes.status = fields.status;
    if (fields.paid_at !== this.initialPaidAt) changes.paid_at = fields.paid_at;
    if (fields.comment !== payment.comment) changes.comment = fields.comment;
    // The backend issues a new link itself when the amount changes, so only other details or turning it on/off go
    const saved = payment.x_data?.invoice ? toInvoiceDetails(payment.x_data.invoice) : null;
    if (fields.invoice !== undefined && JSON.stringify(fields.invoice) !== JSON.stringify(saved)) {
      changes.x_data = { invoice: fields.invoice } satisfies CardPaymentXDataPayload;
    }

    return Object.keys(changes).length ? this.paymentService.updatePayment(payment.id, changes) : null;
  }

  private handleError(error: unknown): void {
    this.paymentService.error(error);
    if (this.paymentService.getErrorCode(error) === 'FISCAL_PREVIEW_CHANGED') {
      this.previewChanged = true;
      this.boardService.requestCardRefresh(this.cardId);
      this.loadPreview();
      return;
    }
    if (this.cashRegisterId.value) this.loadPreview();
    if (!isStalePaymentError(this.paymentService.getErrorCode(error))) return;

    // Nothing can be saved over a payment that changed: the form lets go and the card shows the real payments
    this.paymentService
      .getCardPayments(this.cardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (block) => this.close.emit(block),
        error: (reloadError) => this.paymentService.error(reloadError),
      });
  }

  private patchPayment(payment: CardPayment): void {
    const paidAt = toPickerValues(payment.paid_at);
    this.form.reset({
      payment_type_id: payment.payment_type_id,
      payment_service_id: payment.payment_service_id,
      amount: payment.amount.toFixed(2),
      status: payment.status === 'paid' ? 'paid' : 'pending',
      paid_date: paidAt.date,
      paid_time: paidAt.time,
      comment: payment.comment ?? '',
    });
    if (paidAt.date) this.form.controls.paid_time.enable();
    this.initialPaidAt = fromPickerValues(paidAt.date, paidAt.time);

    const invoice = payment.x_data?.invoice;
    if (invoice) {
      this.form.controls.invoice_enabled.setValue(true, { emitEvent: false });
      this.form.controls.invoice.reset(toInvoiceDetails(invoice));
    }
  }

  /**
   * The details of the invoice for the backend: they are only sent for a service that can issue it, null when it is
   * turned off. The link, the amount and the term are the backend's
   */
  private getInvoiceDetails(): PaymentInvoiceDetails | null | undefined {
    if (!this.invoiceService || !this.isInvoiceCurrency) return undefined;

    return this.form.controls.invoice_enabled.value ? toInvoiceDetails(this.form.controls.invoice.getRawValue()) : null;
  }

  /** A compatible service supplies invoice details without changing the selected payment type. */
  private onServicePicked(): void {
    const settings = this.invoiceService?.x_data?.settings;
    if (!settings) {
      this.form.controls.invoice_enabled.setValue(false);
      return;
    }

    this.form.controls.invoice.reset(toInvoiceDetails(settings));
    this.form.controls.invoice_enabled.setValue(true, { emitEvent: false });
    this.syncInvoice();
  }

  private syncService(): void {
    const selected = this.form.controls.payment_service_id.value;
    if (selected && !this.serviceOptions.some((option) => option.id === selected)) {
      this.form.controls.payment_service_id.setValue(null);
    }
  }

  /** The details are validated only while the invoice is on and the service can issue it */
  private syncInvoice(): void {
    const details = this.form.controls.invoice;
    const editable = this.form.controls.invoice_enabled.value && !!this.invoiceService && this.isInvoiceCurrency;
    if (editable) details.enable({ emitEvent: false });
    else details.disable({ emitEvent: false });
  }

  /** A picked date starts at midnight and a cleared one takes its time with it */
  private syncPaidTime(date: string | null): void {
    const time = this.form.controls.paid_time;
    if (date) {
      time.enable({ emitEvent: false });
      if (!time.value) time.setValue('00:00', { emitEvent: false });
      return;
    }

    time.reset('', { emitEvent: false });
    time.disable({ emitEvent: false });
  }

  private getOptions(active: PaymentOption[], current: PaymentOption | null | undefined): PaymentOption[] {
    const options = [...active];
    if (current && !options.some((option) => option.id === current.id)) {
      options.push({ id: current.id, name: `${current.name} (${this.paymentService.t('board_payments.inactive')})` });
    }
    return options;
  }
}
